import { Loader } from '@10xLabs/react-web/components/Loader'
import { useQuery, NetworkStatus } from '@apollo/client'
import { ReactElement, useEffect, useMemo } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { GET_ROUTING_PROJECT } from 'features/RoutingProjects/queries'
import { Button } from 'components/v2/Button'
import { createRoutingProjectModel } from 'features/RoutingProjects/models'
import { RoutingProjectFragment } from 'gql/generated/graphql'
import { RoutingProjectPlanContainer } from 'features/RoutingProjects/containers/PlanContainer'

const StyledContent = styled(Content)`
  height: calc(100% - 60px);
  padding: 30px 20px;
  display: grid;
  grid-template-rows: min-content 1fr;
`

const StyledContentBody = styled(ContentBody)`
  height: 0;
  min-height: 100%;
`

const Section = styled.section`
  height: 100%;
`

const messages = defineMessages({
  helmet: {
    id: 'routesPlanning.helmet',
    defaultMessage: 'Routes planning | Nexbus Admin'
  }
})

const RoutingProjectView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { routingProjectId } = useParams()

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsScript()

  const {
    data: routeProjectData,
    loading: routeProjectLoading,
    error: routeProjectError,
    refetch: routeProjectRefetch,
    networkStatus: routeProjectNetworkStatus
  } = useQuery(GET_ROUTING_PROJECT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: routingProjectId
      }
    }
  })

  const routingProjectModel = useMemo(() => {
    return createRoutingProjectModel(
      routeProjectData?.routingProject.routingProject as RoutingProjectFragment | undefined
    )
  }, [routeProjectData])

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])

  if (!canRead(FEATURES.ROUTE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ROUTE} />
  }

  const loading = routeProjectLoading || routeProjectNetworkStatus === NetworkStatus.refetch || !isGoogleMapsLoaded

  if (loading) {
    return (
      <Content>
        <ContentBody>
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        </ContentBody>
      </Content>
    )
  } else if (routeProjectError) {
    return (
      <Content>
        <ContentBody>
          <EmptyState.Container>
            <EmptyState.Description>
              <FormattedMessage
                id="routingProject.error"
                defaultMessage="An error occurred while fetching the project. Please try again."
              />
            </EmptyState.Description>
            <EmptyState.Action>
              <Button
                onClick={(): void => {
                  routeProjectRefetch()
                }}
              >
                <FormattedMessage id="routingProject.retry" defaultMessage="Retry" />
              </Button>
            </EmptyState.Action>
          </EmptyState.Container>
        </ContentBody>
      </Content>
    )
  }

  return (
    <StyledContent>
      <ContentHeader isResponsive>
        <Heading.H3>{routingProjectModel.name || 'Project'}</Heading.H3>
      </ContentHeader>
      <StyledContentBody noPadding>
        <Section>
          <RoutingProjectPlanContainer routingProjectModel={routingProjectModel} />
        </Section>
      </StyledContentBody>
    </StyledContent>
  )
}

export default withLastUrl(RoutingProjectView)
